import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, MessageCircle, Phone } from 'lucide-react';
import { CONTACT } from '../data/mock';
import { trackCallClick, trackWhatsAppClick, trackBookButtonClick } from '../lib/analytics';

/**
 * StickyMobileCta
 * Sticky bottom CTA bar shown ONLY on mobile (below `sm` breakpoint).
 * Includes a mobile-only spacer so page content never hides behind the fixed bar.
 * Safe-area aware for iPhone notch/home-indicator via env(safe-area-inset-bottom).
 *
 * Scroll behavior:
 *  - Hidden when the user is at the very top of the page (< 80px).
 *  - Slides up once the user scrolls past the hero (> 60% of viewport height).
 *  - Slides back down when the user returns to the very top.
 *
 * Usage: <StickyMobileCta source="pet_surgery" />
 */
const StickyMobileCta = ({ source = 'sticky_mobile' }) => {
  const wa = `https://wa.me/${CONTACT.phoneRaw.replace(/\D/g, '')}?text=${encodeURIComponent("Hi, I'd like to book an appointment at Ichi & Ori Pet Care.")}`;

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    document.body.classList.add('has-sticky-cta');
    return () => document.body.classList.remove('has-sticky-cta');
  }, []);

  useEffect(() => {
    const revealThreshold = () => Math.max(320, window.innerHeight * 0.6);
    const hideThreshold = 80;

    const onScroll = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      setVisible((prev) => {
        if (!prev && y > revealThreshold()) return true;
        if (prev && y < hideThreshold) return false;
        return prev;
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <>
      {/* Mobile-only spacer prevents content from hiding under the fixed bar */}
      <div className="sm:hidden h-[76px]" aria-hidden="true" />

      {/* Fixed bottom bar, mobile only */}
      <nav
        aria-label="Quick actions"
        aria-hidden={!visible}
        data-testid="sticky-mobile-cta"
        data-visible={visible ? 'true' : 'false'}
        className={`sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-[#f3d6f5] shadow-[0_-6px_20px_rgba(26,11,61,0.10)] pb-[env(safe-area-inset-bottom)] transform-gpu transition-[transform,opacity] duration-500 ease-out will-change-transform ${
          visible ? 'translate-y-0 opacity-100 pointer-events-auto' : 'translate-y-full opacity-0 pointer-events-none'
        }`}
      >
        <div className="grid grid-cols-3 gap-2 px-3 py-2.5">
          <Link
            to="/book"
            onClick={() => trackBookButtonClick(`${source}_sticky_mobile`)}
            data-testid="sticky-mobile-book-btn"
            className="inline-flex items-center justify-center gap-1.5 h-[46px] rounded-full bg-[#e870b6] hover:bg-[#d85ba0] text-white text-[13px] font-semibold shadow-md transition-colors"
          >
            <CalendarDays className="w-4 h-4" />
            Book
          </Link>
          <a
            href={wa}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackWhatsAppClick(`${source}_sticky_mobile`)}
            data-testid="sticky-mobile-wa-btn"
            className="inline-flex items-center justify-center gap-1.5 h-[46px] rounded-full bg-[#25d366] hover:bg-[#1eb858] text-white text-[13px] font-semibold shadow-md transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </a>
          <a
            href={`tel:${CONTACT.phoneRaw}`}
            onClick={() => trackCallClick(`${source}_sticky_mobile`)}
            data-testid="sticky-mobile-call-btn"
            className="inline-flex items-center justify-center gap-1.5 h-[46px] rounded-full bg-white text-[#1a0b3d] border border-[#1a0b3d] hover:bg-[#fdf7fb] text-[13px] font-semibold transition-colors"
          >
            <Phone className="w-4 h-4" />
            Call
          </a>
        </div>
      </nav>
    </>
  );
};

export default StickyMobileCta;
